"use client";

import { motion, useInView } from "framer-motion";
import { ArrowRight } from "lucide-react";
import Image from "next/image";
import Link from "next/link";
import { useRef } from "react";
import { Button } from "@/components/ui/button";
import { IMAGES } from "@/lib/constants/images";

export function EatLikeYourLifeSection() {
	const ref = useRef<HTMLElement>(null);
	const isInView = useInView(ref, { once: true, margin: "-100px" });

	return (
		<section ref={ref} className="relative py-28 md:py-40 overflow-hidden">
			<div className="absolute inset-0">
				<Image
					src={IMAGES.home.eatLikeYourLifeDependsOnIt}
					alt="Ice Age Meals — Eat Like Your Life Depends On It"
					fill
					className="object-cover"
					sizes="100vw"
					quality={75}
					priority={false}
				/>
				<div className="absolute inset-0 bg-linear-to-t from-black/80 via-black/50 to-black/30" />
			</div>

			<div className="relative z-10 container mx-auto px-4 flex flex-col items-center justify-center text-center">
				<div className="max-w-3xl mx-auto space-y-6">
					<motion.h2
						initial={{ opacity: 0, y: 30 }}
						animate={isInView ? { opacity: 1, y: 0 } : {}}
						transition={{ duration: 0.6 }}
						className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-black tracking-tight text-white leading-[1.1] drop-shadow-lg"
					>
						EAT LIKE YOUR LIFE{" "}
						<span className="text-amber-400 drop-shadow-md">DEPENDS ON IT</span>
					</motion.h2>

					<motion.p
						initial={{ opacity: 0, y: 20 }}
						animate={isInView ? { opacity: 1, y: 0 } : {}}
						transition={{ duration: 0.5, delay: 0.15 }}
						className="text-base sm:text-lg md:text-xl text-zinc-200 leading-relaxed max-w-2xl mx-auto"
					>
						Because it does. What you eat shapes how you feel, how you perform,
						and how you show up every single day. Choose meals that fuel the
						life you want.
					</motion.p>

					<motion.div
						initial={{ opacity: 0, y: 20 }}
						animate={isInView ? { opacity: 1, y: 0 } : {}}
						transition={{ duration: 0.5, delay: 0.3 }}
					>
						<Button
							size="lg"
							className="h-14 px-10 rounded-full font-bold text-base group shadow-xl"
							asChild
						>
							<Link href="/build-a-box">
								Build a Box
								<ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
							</Link>
						</Button>
					</motion.div>
				</div>
			</div>
		</section>
	);
}
